import {
	LIST_ITEM_KEY,
	ORDERED_LIST_KEY,
	UNORDERED_LIST_KEY,
	PARAGRAPH_KEY
} from '@techchange/pm-specs';

import { groupNodesByType } from '../../utils/groupNodesByType';
import { mergeLists } from '../../utils/mergeLists';
import { splitNodes } from '../../utils/splitNodes';

/**
 * Recursively iterates through a list and creates a new list that has unwrapped
 * or toggled the list type of the selected content
 *
 * @param   {Object}  listType         The requested list type for the selection
 * @param   {Object}  node             The list node
 * @param   {number}  listPos          The position of the list in the document
 * @param   {Object}  state            The document state
 * @param   {Object}  options          Set of options to customize behavior
 * @param   {boolean} options.merge    Specifies whether or not to merge nodes into list of listType
 * @param   {boolean} options.unwrap   Specifies whether or not to unwrap all
 *                                     selected paragraph nodes
 *
 * @returns {Object} The list of nodes to replace the list
 */
export const rebuildList = (listType, node, listPos, state, options = {}) => {

	const { merge, unwrap } = options;

	const { schema, selection } = state;

	// The beginning and end positions of the selection
	const { from, to } = selection;

	// Node type constants
	const paragraphType = schema.nodes[PARAGRAPH_KEY];
	const listItemType = schema.nodes[LIST_ITEM_KEY];
	const orderedListType = schema.nodes[ORDERED_LIST_KEY];
	const unorderedListType = schema.nodes[UNORDERED_LIST_KEY];

	const beforeContent = [], selectedContent = [], afterContent = [];

	let newNodes = [];

	// Traverse through list item nodes
	node.descendants((child, childPos) => {

		// Calculate the absolute position of the child node
		const listItemPos = listPos + 1 + childPos;

		const paragraphContent = [];

		let listContent = [];

		child.descendants((grandchild, grandChildPos) => {

			// Calculate the absolute position of the grandchild node
			const docOffset = listItemPos + 1 + grandChildPos;

			if (grandchild.type === orderedListType || grandchild.type === unorderedListType) {

				const nestedContent =
					rebuildList(listType, grandchild, docOffset, state, { merge, unwrap });

				listContent = listContent.concat(nestedContent);

			} else {

				paragraphContent.push({ node: grandchild, pos: docOffset });

			}

			return false;

		});

		// Re-wrap or unwrap children in appropriate list-item grouping
		const paragraphNodes = paragraphContent.map((paragraph) => paragraph.node);

		let newListItems = [];

		// Determine if any paragraph content is selected
		const firstParagraph = paragraphContent[0];
		const lastParagraph = paragraphContent[paragraphContent.length - 1];
		const paragraphStart = firstParagraph.pos;
		const paragraphEnd = lastParagraph.pos + lastParagraph.node.nodeSize;
		const isSelected = paragraphEnd > from && paragraphStart <= to;

		// Case 1: Unwrap the paragraph node if toggling its parent list type
		// OR unwrap all selected paragraph child nodes
		if (isSelected && !merge && (listType === node.type || unwrap)) {

			newListItems = newListItems.concat(paragraphNodes);

		// Case 2: Re-wrap the paragraph node in a list-item node
		} else {

			// Check if there is any nested content that should wrapped in the list-item
			// with the paragraph node. This would be any adjacent list nodes
			// that come before the first unwrapped paragraph node in listContent.
			let listItem;

			const listBreakIndex =
				listContent.findIndex((contentNode) => contentNode.type === paragraphType);

			// Case 2a: No nested content or no lists before the first unwrapped paragraph node,
			// wrap the paragraph node in a list-item by itself
			if (listContent.length === 0 || listBreakIndex === 0) {

				listItem = listItemType.create({}, paragraphNodes);

			// Case 2b: Nested content contains lists before the first unwrapped paragraph node,
			// wrap the paragraph node in a list-item with the first group
			} else if (listBreakIndex > 0) {

				const subLists = listContent.slice(0, listBreakIndex);
				const mergedSubLists = groupNodesByType(subLists).map(mergeLists);

				listItem = listItemType.create({}, [...paragraphNodes, ...mergedSubLists]);
				listContent = listContent.slice(listBreakIndex);

			// Case 2c: Nested content does not contain any unwrapped paragraph nodes,
			// wrap the paragraph node in a list-item with all of the nested content
			} else {

				const mergedListContent = groupNodesByType(listContent).map(mergeLists);

				listItem = listItemType.create({}, [...paragraphNodes, ...mergedListContent]);
				listContent = [];

			}

			newListItems.push(listItem);

		}

		// Wrap any remaining nested content in a list-item
		// (ignore unwrapped paragraph nodes)
		const wrappedContent = splitNodes(listContent, paragraphType).map((group) => {

			if (Array.isArray(group)) {

				const placeholder = paragraphType.create({}, '');
				const mergedListGroup = groupNodesByType(group).map(mergeLists);

				return listItemType.create({}, [placeholder, ...mergedListGroup]);

			}

			return group;

		});

		newListItems = newListItems.concat(wrappedContent);

		// Place list items in the corresponding bucket based on the
		// position of the root paragraph node of the list-item
		newListItems.forEach((listItem) => {

			if (paragraphEnd <= from) {

				beforeContent.push(listItem);

			} else if (paragraphStart > to) {

				afterContent.push(listItem);

			} else {

				selectedContent.push(listItem);

			}

		});

		// Don't recurse to child nodes
		return false;

	});

	// Wrap any consecutive list-item nodes into a single list
	// (ignore unwrapped paragraph nodes)
	if (node.type === listType) {

		const nodeList = [...beforeContent, ...selectedContent, ...afterContent];

		newNodes = newNodes.concat(splitNodes(nodeList, paragraphType).map((group) => {

			if (Array.isArray(group)) {

				return listType.create({}, group);

			}

			return group;

		}));

	} else {

		const bucketTypes = {
			before: {
				list: beforeContent,
				type: node.type,
			},
			selected: {
				list: selectedContent,
				type: listType,
			},
			after: {
				list: afterContent,
				type: node.type,
			},
		};

		Object.values(bucketTypes).forEach((bucket) => {

			const contentNodes = splitNodes(bucket.list, paragraphType).map((group) => {

				if (Array.isArray(group)) {

					return bucket.type.create({}, group);

				}

				return group;

			});

			newNodes = newNodes.concat(contentNodes);

		});

	}

	return newNodes;

};
